// ═══════════════════════════════════════════════════════════════
// studioImagesApi.js — Service API Studio Images (S52)
// ═══════════════════════════════════════════════════════════════

import { handleUpsell } from '../utils/upsell';

const getToken = () => localStorage.getItem('oracle_token');

const jsonHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
});

const authHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
});

/**
 * Récupère les modèles disponibles pour Studio Images (registry).
 */
export async function getStudioImageModels() {
    const r = await fetch('/api/capabilities/models?context=studio-images', { headers: authHeaders() });
    if (!r.ok) throw new Error(`Erreur récupération modèles (${r.status})`);
    const data = await r.json();
    return data.models || [];
}

/**
 * Récupère l'historique de génération Studio (scope user).
 */
export async function getHistory({ projectId = null, limit = 20, offset = 0, search = null, status = 'all' } = {}) {
    const params = new URLSearchParams({ limit, offset, status });
    if (projectId) params.set('project_id', projectId);
    if (search) params.set('search', search);
    const r = await fetch(`/api/studio/images/history?${params}`, { headers: authHeaders() });
    if (!r.ok) throw new Error(`Erreur historique (${r.status})`);
    return r.json();
}

/**
 * Supprime une entrée historique (soft delete).
 */
export async function deleteGeneration(id) {
    const r = await fetch(`/api/studio/images/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (!r.ok) throw new Error(`Erreur suppression (${r.status})`);
    return r.json();
}

/**
 * Exporte une génération vers un projet (attache l'asset).
 */
export async function exportToProject(generationId, projectId, filename = null) {
    const r = await fetch(`/api/studio/images/${generationId}/export-to-project`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ project_id: projectId, filename }),
    });
    if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'export_failed' }));
        const e = new Error(err.message || err.error || `HTTP ${r.status}`);
        e.code = err.error;
        e.status = r.status;
        throw e;
    }
    return r.json();
}

/**
 * Récupère la liste des projets de l'utilisateur (pour sélecteur export).
 */
export async function getProjects() {
    const r = await fetch('/api/projects', { headers: authHeaders() });
    if (!r.ok) throw new Error('Erreur récupération projets');
    const d = await r.json();
    return d.projects || d || [];
}

/**
 * Lance une génération et consomme le SSE stream.
 * Retourne un AbortController permettant d'annuler côté client.
 *
 * @param {Object} params - payload génération
 * @param {Object} handlers - { onStatus, onImage, onDone, onError }
 * @returns {AbortController}
 */
export function generateImage(params, { onStatus, onImage, onDone, onError } = {}) {
    const controller = new AbortController();

    (async () => {
        try {
            const response = await fetch('/api/studio/images/generate', {
                method: 'POST',
                headers: jsonHeaders(),
                body: JSON.stringify(params),
                signal: controller.signal,
            });

            if (!response.ok && response.status !== 200) {
                const err = await response.json().catch(() => ({ error: 'request_failed' }));
                // Feature réservée aux paliers payants → toast upsell dédié.
                handleUpsell(err);
                onError && onError({ code: err.error, message: err.message, http_status: response.status });
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                // Parse SSE events (double-newline delimited)
                let idx;
                while ((idx = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);

                    let eventName = 'message';
                    let dataStr = '';
                    for (const line of rawEvent.split('\n')) {
                        if (line.startsWith('event: ')) eventName = line.slice(7).trim();
                        else if (line.startsWith('data: ')) dataStr += line.slice(6);
                    }
                    if (!dataStr) continue;

                    let data;
                    try { data = JSON.parse(dataStr); } catch { continue; }

                    if (eventName === 'status') onStatus && onStatus(data);
                    else if (eventName === 'image') onImage && onImage(data);
                    else if (eventName === 'done') onDone && onDone(data);
                    else if (eventName === 'error') onError && onError(data);
                }
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                onError && onError({ code: 'aborted', message: 'Génération annulée' });
            } else {
                onError && onError({ code: 'network', message: err.message });
            }
        }
    })();

    return controller;
}
